import type { PropsWithChildren } from 'react'
import {
  type ComponentPropsWithoutRef,
  type ElementRef,
  forwardRef,
  useId,
} from 'react'

import * as RadioGroupPrimitive from '@radix-ui/react-radio-group'

import { classNames } from '@/utils/helpers'

type RadioProps = ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> & {
  label: string
  labelClassName?: string
  buttonClassName?: string
}

export const Radio = forwardRef<
  ElementRef<typeof RadioGroupPrimitive.Item>,
  RadioProps
>(({ label, className, labelClassName, buttonClassName, ...props }, ref) => {
  const baseId = useId()

  return (
    <label
      className={classNames(
        'group/item flex cursor-pointer items-center space-x-2',
        { 'pointer-events-none opacity-50': props.disabled },
        className,
      )}
      htmlFor={baseId}
    >
      <RadioGroupPrimitive.Item
        {...props}
        className={classNames(
          'h-4 w-4 shrink-0 rounded-full border border-secondary-100 transition group-hover/item:border-primary-500 data-[state=checked]:border-[6px] data-[state=checked]:border-primary-500',
          buttonClassName,
        )}
        id={baseId}
        ref={ref}
      >
        <RadioGroupPrimitive.Indicator className="RadioGroupIndicator" />
      </RadioGroupPrimitive.Item>
      <div className={classNames('text-title', labelClassName)}>{label}</div>
    </label>
  )
})
Radio.displayName = RadioGroupPrimitive.Item.displayName

type RadioGroupProps = Omit<
  ComponentPropsWithoutRef<PropsWithChildren<typeof RadioGroupPrimitive.Root>>,
  'onChange'
> & {
  onChange: (value: string) => void
}

export const RadioGroup = forwardRef<
  ElementRef<typeof RadioGroupPrimitive.Root>,
  RadioGroupProps
>(({ onChange, ...props }, ref) => (
  <RadioGroupPrimitive.Root
    onValueChange={onChange}
    {...props}
    ref={ref}
  />
))
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName
